import type {
  userInfoInterface,
  levelExperienceInterface
} from '@/types/apiType/userType'
import type { userInfoStorageMapInterface } from '@/types/viewsType/userType'
import {
  apiGetUserInfo,
  apiGetUserLevelExperience,
  apiUpdateLanguage
} from '@/api/user'
import { apiManOnline } from '@/api/chat'
import * as Sentry from '@sentry/nuxt'

export const useUserStore = defineStore({
  id: 'user',
  state: () => ({
    userInfo: {} as userInfoInterface, // 用户信息
    userInfoStorageMap: {} as { [uid: string]: userInfoStorageMapInterface }, // 用户信息列表Map，每次获取到用户信息，都要更新一下
    levelExperienceInfo: {} as levelExperienceInterface, // 等级
    /** 用户信息弹窗配置 */
    userInfoPopupConfig: {
      /** 是否显示 */
      isShow: false as boolean,
      /** uid */
      uid: 0 as number,
      /** 是否显示通话 */
      isCall: true as boolean
    }
  }),
  actions: {
    /** 关闭用户信息弹窗 */
    closeUserInfoPopup() {
      this.userInfoPopupConfig.isShow = false
    },
    /**
     * 打开指定用户信息弹窗
     * @param {string | number} 打开用户信息的uid
     * @param {config:{isCall:boolean}} 配置信息 { isCall:是否显示通话}
     *  */
    openUserInfoPopup(uid: string | number, config?: { isCall: boolean }) {
      console.log(uid)
      if (!uid) return
      const roomStore = useRoomStore()
      if (roomStore.isApp) {
        const userInfo: userInfoStorageMapInterface = JSON.parse(
          JSON.stringify(this.userInfoStorageMap[uid] || { uid })
        )
        // 转数据给App
        if (!userInfo.experLevel && userInfo.videoRoomExperLevel) {
          userInfo.experLevel = userInfo.videoRoomExperLevel
          userInfo.experLevelPic = userInfo.videoRoomExperLevelPic
        }
        // 打开用户弹窗
        AppNative.appFun(
          AppNative.iosNeedMessageFun.userPopup,
          JSON.stringify(userInfo)
        )
      } else {
        this.userInfoPopupConfig.uid = Number(uid)
        if (config) {
          this.userInfoPopupConfig.isCall = config.isCall
        } else {
          this.userInfoPopupConfig.isCall = true
        }
        this.userInfoPopupConfig.isShow = true
      }
    },
    /**
     * 获取用户等级
     */
    async getLevelExperienceInfo() {
      const res = await apiGetUserLevelExperience({ type: 3 })
      const { code, data } = res
      if (code === 200) {
        this.levelExperienceInfo = data
      }
    },
    /**
     * 用户上线下线调用，后端的自动打招呼功能需要使用，使用localStorage的uid是为了保证退登前使用到
     * @param {1 | 2} type // 1上线 2下线
     * */
    async changeManOnline(type: 1 | 2) {
      const loginStr = localStorage.getItem('loginInfo')
      if (loginStr) {
        const loginInfo = JSON.parse(loginStr)
        await apiManOnline({ type, uid: loginInfo.uid })
      }
    },
    // 公共方法，设置指定用户信息
    publicSetUserInfo(info: userInfoStorageMapInterface) {
      // const length = Object.keys(this.userInfoStorageMap).length
      // console.log(length)
      // if (length > 10) {
      //   console.log('已清楚')
      //   this.userInfoStorageMap = {}
      // }
      if (info && info.uid) {
        if (this.userInfoStorageMap[info.uid]) {
          this.userInfoStorageMap[info.uid] = {
            ...this.userInfoStorageMap[info.uid],
            ...info
          }
        } else {
          this.userInfoStorageMap[info.uid] = { ...info }
        }
      }
    },
    // 公共方法获取指定用户信息
    async publicGetUserInfo(queryUid: string | number) {
      const res = await apiGetUserInfo({ queryUid })
      const { code, data } = res
      if (code === 200) {
        this.publicSetUserInfo(data)
        return Promise.resolve(data as userInfoInterface)
      } else {
        return Promise.reject(false)
      }
    },
    // 更新用户信息列表Map
    // const userInfoStorage = computed(() => userStore.userInfoStorageMap[targetUid.value] as userInfoStorageMapInterface || {})
    updateUserInfoStorageMap(config: {
      type: 'init' | 'put'
      list: userInfoStorageMapInterface[]
    }) {
      switch (config.type) {
        case 'init':
          this.userInfoStorageMap = {}
          break
        case 'put':
          config.list.forEach((v) => {
            this.publicSetUserInfo(v)
          })
          break
      }
    },
    // 不请求接口设置用户数据
    setUserInfo(info: userInfoInterface) {
      const languageStore = useLanguageStore()

      this.userInfo = info
      languageStore.initLanguage() // 初始化语言
      this.publicSetUserInfo(info)
      // sentry监控设置用户信息
      Sentry.setUser({
        username: this.userInfo.nick,
        id: this.userInfo.erbanNo
      })
    },
    /**
     * 请求接口更新数据
     * avatarLoad:boolean 传true就像预加载
     * */
    async requestUserInfo(config?: { avatarLoad?: boolean }) {
      try {
        if (localStorage.getItem('loginInfo')) {
          const queryUid = JSON.parse(
            localStorage.getItem('loginInfo') as string
          ).uid
          const res = await apiGetUserInfo({ queryUid })
          // eslint-disable-next-line prefer-const
          let { code, data } = res
          if (code === 200) {
            // 极端特殊情况判断：如果用户没有language，就先配置第一语言再次获取数据。用于用户没有第一语言，一般情况下在登录都会有第一语言的了
            if (!data.language) {
              const languageStore = useLanguageStore()
              await apiUpdateLanguage({
                updateLanguage: languageStore.languageInfo.text,
                type: 1
              }) // 配置第一语言
              const resAgain = await apiGetUserInfo({ queryUid })
              const { code: codeAgain, data: dataAgain } = resAgain
              if (codeAgain === 200) {
                data = dataAgain
              }
            }

            this.setUserInfo(data)

            // 如果需要头像预加载
            if (config?.avatarLoad) {
              new Image().src = common.handlePicUrlAvatar(data.avatar as string)
            }
            return true
          }
          return false
        }
      } catch (error) {
        console.log(error)
      }
    },

    // 重置数据
    resetData() {
      this.userInfo = defaultData.defaultUserInfo()
      this.userInfoStorageMap = {}
      this.levelExperienceInfo = defaultData.defaultLevelExperienceInfo()
      this.userInfoPopupConfig.isShow = false
      // 清除sentry当前设置的用户
      Sentry.setUser(null)
    }
  },
  persist: {
    key: 'userStore',
    paths: ['userInfo', 'userInfoStorageMap', 'levelExperienceInfo']
  }
})
